import { useEffect, useState } from 'react';
import { dashboardApi } from '@/features/dashboard/api/dashboardApi';
import type { DashboardDto } from '@/shared/types/domain';
import { ApiError } from '@/shared/api/httpClient';

interface UseDashboardStatsResult {
  stats: DashboardDto | null;
  isLoading: boolean;
  error: string | null;
}

export function useDashboardStats(exhibitorId: string | undefined): UseDashboardStatsResult {
  const [stats, setStats] = useState<DashboardDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!exhibitorId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    // The dashboard endpoint is keyed by eventId, but the client only knows
    // its own exhibitorId (from the JWT claims) -- resolve the exhibitor's
    // parent event first, then fetch stats scoped to it.
    dashboardApi
      .getExhibitor(exhibitorId)
      .then((exhibitor) => dashboardApi.getDashboard(exhibitor.eventId))
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.userMessage : 'Could not load dashboard stats.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [exhibitorId]);

  return { stats, isLoading, error };
}
